import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  ChevronLeft,
  ChevronRight,
  Heart,
  Maximize2,
  Share2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import './styles.css';

interface VehicleGalleryProps {
  photos: string[];
  alt: string;
  isZeroKm?: boolean;
  isFavorited: boolean;
  onFavorite: () => void;
  onShare: () => void;
}

/** Slots na coluna lateral; o último vira +X quando há mais fotos. */
const RAIL_MAX_SLOTS = 8;

export function VehicleGallery({
  photos,
  alt,
  isZeroKm,
  isFavorited,
  onFavorite,
  onShare,
}: VehicleGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);
  const [isFullscreenLoading, setIsFullscreenLoading] = useState(false);
  const fullscreenStripRef = useRef<HTMLDivElement>(null);

  const total = photos.length;
  const hasMultiple = total > 1;
  const showRailMore = total > RAIL_MAX_SLOTS;
  const railThumbCount = showRailMore ? RAIL_MAX_SLOTS - 1 : total;
  const railMoreCount = showRailMore ? total - railThumbCount : 0;
  const railPhotos = photos.slice(0, railThumbCount);

  const selectImage = useCallback((index: number) => {
    if (index === currentIndex) return;
    setIsLoading(true);
    setCurrentIndex(index);
  }, [currentIndex]);

  const goNext = useCallback(() => {
    if (isLoading || !hasMultiple) return;
    setIsLoading(true);
    setCurrentIndex((prev) => (prev === total - 1 ? 0 : prev + 1));
  }, [hasMultiple, isLoading, total]);

  const goPrev = useCallback(() => {
    if (isLoading || !hasMultiple) return;
    setIsLoading(true);
    setCurrentIndex((prev) => (prev === 0 ? total - 1 : prev - 1));
  }, [hasMultiple, isLoading, total]);

  const openFullscreen = useCallback((index: number) => {
    setFullscreenIndex(index);
    setIsFullscreen(true);
  }, []);

  const closeFullscreen = useCallback(() => {
    setCurrentIndex(fullscreenIndex);
    setIsFullscreen(false);
  }, [fullscreenIndex]);

  const goFullscreenNext = useCallback(() => {
    if (isFullscreenLoading || !hasMultiple) return;
    setIsFullscreenLoading(true);
    setFullscreenIndex((prev) => (prev === total - 1 ? 0 : prev + 1));
  }, [hasMultiple, isFullscreenLoading, total]);

  const goFullscreenPrev = useCallback(() => {
    if (isFullscreenLoading || !hasMultiple) return;
    setIsFullscreenLoading(true);
    setFullscreenIndex((prev) => (prev === 0 ? total - 1 : prev - 1));
  }, [hasMultiple, isFullscreenLoading, total]);

  useEffect(() => {
    if (!isFullscreen) return;

    const thumb = fullscreenStripRef.current?.querySelector<HTMLElement>(
      `[data-strip-index="${fullscreenIndex}"]`
    );
    thumb?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [fullscreenIndex, isFullscreen]);

  useEffect(() => {
    if (!isFullscreen) return;

    const scrollY = window.scrollY;
    const { style } = document.body;
    const previousOverflow = style.overflow;
    const previousPaddingRight = style.paddingRight;
    const previousPosition = style.position;
    const previousTop = style.top;
    const previousWidth = style.width;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    style.overflow = 'hidden';
    style.position = 'fixed';
    style.top = `-${scrollY}px`;
    style.width = '100%';
    if (scrollbarWidth > 0) {
      style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      style.overflow = previousOverflow;
      style.position = previousPosition;
      style.top = previousTop;
      style.width = previousWidth;
      style.paddingRight = previousPaddingRight;
      window.scrollTo(0, scrollY);
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (!isFullscreen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeFullscreen();
      if (e.key === 'ArrowRight') goFullscreenNext();
      if (e.key === 'ArrowLeft') goFullscreenPrev();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeFullscreen, goFullscreenNext, goFullscreenPrev, isFullscreen]);

  if (!total) return null;

  return (
    <>
      <div className="wl3-gallery">
        <div className="wl3-gallery__frame">
          <div className={cn('wl3-gallery__body', hasMultiple && 'wl3-gallery__body--with-rail')}>
            {hasMultiple && (
              <div className="wl3-gallery__thumbs">
                {railPhotos.map((photo, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => selectImage(index)}
                    className={cn(
                      'wl3-gallery__thumb',
                      index === currentIndex && 'wl3-gallery__thumb--active'
                    )}
                    aria-label={`Foto ${index + 1}`}
                    aria-current={index === currentIndex}
                  >
                    <img src={photo} alt="" loading="lazy" />
                  </button>
                ))}

                {showRailMore && (
                  <button
                    type="button"
                    onClick={() => openFullscreen(currentIndex)}
                    className={cn(
                      'wl3-gallery__thumb wl3-gallery__thumb--more',
                      currentIndex >= railThumbCount && 'wl3-gallery__thumb--active'
                    )}
                    aria-label={`Ver mais ${railMoreCount} fotos`}
                  >
                    <img
                      src={photos[railThumbCount]}
                      alt=""
                      className="wl3-gallery__thumb-img--dimmed"
                      loading="lazy"
                    />
                    <span className="wl3-gallery__thumb-more-label">+{railMoreCount}</span>
                  </button>
                )}
              </div>
            )}

            <div className="wl3-gallery__hero group">
            {isLoading && (
              <div className="wl3-gallery__loader" aria-hidden>
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-[var(--wl3-accent,hsl(var(--primary)))]" />
              </div>
            )}

            <img
              key={currentIndex}
              src={photos[currentIndex]}
              alt={alt}
              className={cn(
                'wl3-gallery__hero-img',
                isLoading && 'wl3-gallery__hero-img--loading'
              )}
              onLoad={() => setIsLoading(false)}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                setIsLoading(false);
              }}
            />

            <button
              type="button"
              onClick={() => !isLoading && openFullscreen(currentIndex)}
              disabled={isLoading}
              className="wl3-gallery__hero-zoom"
              aria-label="Ampliar foto"
            />

            {isZeroKm && (
              <Badge className="absolute top-3 left-3 z-20 bg-white text-foreground shadow-sm border-0">
                Zero KM
              </Badge>
            )}

            <div className="wl3-gallery__hero-actions">
              <button
                type="button"
                onClick={onFavorite}
                className="wl3-gallery__action-btn"
                title={isFavorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
              >
                <Heart
                  className={cn(
                    'h-[18px] w-[18px]',
                    isFavorited ? 'fill-red-500 text-red-500' : 'text-slate-600'
                  )}
                />
              </button>
              <button type="button" onClick={onShare} className="wl3-gallery__action-btn">
                <Share2 className="h-[18px] w-[18px] text-slate-600" />
              </button>
            </div>

            {hasMultiple && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  disabled={isLoading}
                  className="wl3-gallery__arrow wl3-gallery__arrow--prev"
                  aria-label="Foto anterior"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  disabled={isLoading}
                  className="wl3-gallery__arrow wl3-gallery__arrow--next"
                  aria-label="Próxima foto"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}

            <div className="wl3-gallery__hero-footer">
              {hasMultiple && (
                <span className="wl3-gallery__counter">
                  {currentIndex + 1} / {total}
                </span>
              )}
              <button
                type="button"
                onClick={() => openFullscreen(currentIndex)}
                disabled={isLoading}
                className="wl3-gallery__expand"
                aria-label="Ver galeria em tela cheia"
              >
                <Maximize2 className="h-4 w-4" />
                {hasMultiple ? 'Ver todas' : 'Ampliar'}
              </button>
            </div>
            </div>
          </div>
        </div>
      </div>

      {isFullscreen && (
        <div
          className="wl3-gallery-lightbox"
          role="dialog"
          aria-modal
          aria-label="Galeria de fotos"
          onClick={closeFullscreen}
        >
          <button
            type="button"
            onClick={closeFullscreen}
            className="wl3-gallery-lightbox__close"
            aria-label="Fechar galeria"
          >
            <X className="h-6 w-6" />
          </button>

          <div className="wl3-gallery-lightbox__meta">
            <span className="wl3-gallery-lightbox__counter">
              {fullscreenIndex + 1} / {total}
            </span>
          </div>

          <div className="wl3-gallery-lightbox__stage">
            {isFullscreenLoading && (
              <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                <div className="animate-spin rounded-full h-14 w-14 border-2 border-white/20 border-t-white" />
              </div>
            )}

            <img
              key={fullscreenIndex}
              src={photos[fullscreenIndex]}
              alt={alt}
              className={cn(
                'wl3-gallery-lightbox__img',
                isFullscreenLoading && 'opacity-40'
              )}
              onClick={(e) => e.stopPropagation()}
              onLoad={() => setIsFullscreenLoading(false)}
            />

            {hasMultiple && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    goFullscreenPrev();
                  }}
                  disabled={isFullscreenLoading}
                  className="wl3-gallery-lightbox__nav wl3-gallery-lightbox__nav--prev"
                  aria-label="Foto anterior"
                >
                  <ChevronLeft className="h-7 w-7" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    goFullscreenNext();
                  }}
                  disabled={isFullscreenLoading}
                  className="wl3-gallery-lightbox__nav wl3-gallery-lightbox__nav--next"
                  aria-label="Próxima foto"
                >
                  <ChevronRight className="h-7 w-7" />
                </button>
              </>
            )}
          </div>

          {hasMultiple && (
            <div className="wl3-gallery-lightbox__strip" onClick={(e) => e.stopPropagation()}>
              <div ref={fullscreenStripRef} className="wl3-gallery-lightbox__strip-inner">
                {photos.map((photo, index) => (
                  <button
                    key={index}
                    type="button"
                    data-strip-index={index}
                    onClick={() => {
                      if (index !== fullscreenIndex) setIsFullscreenLoading(true);
                      setFullscreenIndex(index);
                    }}
                    className={cn(
                      'wl3-gallery-lightbox__strip-thumb',
                      index === fullscreenIndex && 'wl3-gallery-lightbox__strip-thumb--active'
                    )}
                  >
                    <img src={photo} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
